import { Component } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged } from 'rxjs/operators';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { ProcessingManager } from '@managers/processing.manager';
import { TableLikeBaseComponent } from '../../../table-like-base.component';
import { Sort } from '@angular/material/sort';
import { orderBy } from 'lodash-es';
import { Clipboard } from '@angular/cdk/clipboard';
import { NotificationManager } from '@managers/notification.manager';
import { WorkflowStateResource } from "@models/resource/workflow-state.resource";
import { WorkflowSdk } from "@sdks/workflow.sdk";
import { UserConfigsService } from "@services/user-configs.service";
import { rowTransitionAnimation } from "../animations";

@Component({
    selector: 'lex-microservices',
    templateUrl: './active-workflows-table.component.html',
    animations: [rowTransitionAnimation],
    styleUrls: ['./active-workflows-table.component.scss'],
    standalone: false
})
export class ActiveWorkflowsTableComponent extends TableLikeBaseComponent<WorkflowStateResource> {
	public dataSource$: Observable<any>;
	public rowHeight$ = this.processingManager.currentRowHeight$;
	public sort$ = new BehaviorSubject<Sort>({ active: 'progressPercentage', direction: 'desc' });
	public columnsToDisplay = [
		'selecto-checko',
		'status',
		'progressPercentage',
		'name',
		'caseName',
		'currentOperation',
	];

	constructor(
		public processingManager: ProcessingManager,
		public clipboard: Clipboard,
		public notificationManager: NotificationManager,
		public workflowSdk: WorkflowSdk,
		public userConfigsService: UserConfigsService
	) {
		super();
		if(!this.userConfigsService.inRoles(this.roles.Administrator)) {
			this.columnsToDisplay = this.columnsToDisplay.filter(column => column !== 'selecto-checko');
		}
		this.dataSource$ = combineLatest([
			this.processingManager.workflows$,
			this.processingManager.currentFilterText$,
			this.sort$
		]).pipe(
			filter(([collection]) => !!collection),
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map(([workflowCollection, term, sortOptions]) => {
				if (term) {
					workflowCollection = workflowCollection?.filter((item) => {
						return item.workflowName?.toLowerCase().includes(term.toLowerCase());
					});
				}
				if (sortOptions) {
					workflowCollection = orderBy(workflowCollection, sortOptions.active, sortOptions.direction as 'asc' | 'desc');
				}

				workflowCollection = workflowCollection.map((x: any) => {
					x.id = `${x.workflowName}---${x.workflowKey}`;
					return x;
				});
				return new MatTableDataSource(workflowCollection as any);
			})
		);
	}

	public sort(sort: Sort): void {
		this.sort$.next(sort);
	}

	public copyTextToClipboard(text: string) {
		this.clipboard.copy(text);
		this.notificationManager.displaySnackBarMessage({
			message: 'Copied to clipboard!'
		});
	}

	public trackByFn(index: number, item: any) {
		return `${item.workflowName}:${item.workflowKey}`;
	}

	public cancel(workflow: WorkflowStateResource) {
		this.workflowSdk.cancelWorkflow(workflow.workflowName, workflow.workflowKey).subscribe();
	}

	public cancelSelected() {
		this.selectedIds.forEach(x => this.cancelWorkflowById(x));
		this.selectedIds = [];
	}

	private cancelWorkflowById(identifier: string): void {
		const [workflowName, workflowKey] = identifier.split('---');
		this.notificationManager.displaySnackBarMessage({
			message: `Canceling workflow: ${identifier}`
		});
		this.workflowSdk.cancelWorkflow(workflowName, workflowKey).subscribe();
	}

	public projectActiveStep(workflow: WorkflowStateResource) {
		return workflow?.progress?.find((x) => x.executionState !== 'Complete' && x.executionState !== 'Skipped')?.operationName?.split('.')?.reverse()?.[0];
	}
}
